import * as React from "react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router";
import { bindActionCreators } from "redux";
import Tree, { ITree } from "src/components/common/Tree/Tree";
import { PersonnelNotesBreadcrumbs } from "src/helpers/breadcrumbHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as NoteTypesNotesActions from "../../../actions/noteTypeNotes";
import * as PersonnelActions from "../../../actions/personnel";
import { InlineLoading } from "../../../components/common/index";
import { RestrictedLayout } from "../../../components/layouts/index";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { PersonnelSubMenu } from "../../../helpers/subMenuHelper";
import { ITmdState } from "../../../reducers/index";

import "./PersonnelNotesPage.scss";

interface PersonnelNotesPageProps {
    personnel: Dtos.Personnel;
    loadingPersonnel: boolean;
    loadPersonnelSuccess: boolean;
    loadPersonnelFailure: boolean;
    loadPersonnelErrorCode: string;
    noteTypeNotes: Dtos.NoteTypeNotes[];
    loadingNoteTypeNotes: boolean;
    loadNoteTypeNotesSuccess: boolean;
    loadNoteTypeNotesFailure: boolean;
    loadNoteTypeNotesErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearPersonnel: PersonnelActions.IPersonnelClearActionCreator;
    loadPersonnel: PersonnelActions.IPersonnelLoadByIdActionCreator;
    clearNoteTypeNotes: NoteTypesNotesActions.INoteTypeNotesClearActionCreator;
    loadNoteTypeNotes: NoteTypesNotesActions.INoteTypeNotesLoadByPersonnelIdActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const PersonnelNotesPage: FC<PersonnelNotesPageProps> = ({
    personnel,
    loadingPersonnel,
    loadPersonnelSuccess,
    loadPersonnelFailure,
    loadPersonnelErrorCode,
    noteTypeNotes,
    loadingNoteTypeNotes,
    loadNoteTypeNotesSuccess,
    loadNoteTypeNotesFailure,
    loadNoteTypeNotesErrorCode,
    permissions,
    clearPersonnel,
    loadPersonnel,
    clearNoteTypeNotes,
    loadNoteTypeNotes,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { personnelId } = useParams<{ personnelId: string }>();
    const personnelIdNumber = parseInt(personnelId, 10);

    //
    // -- States --
    const [treeData, setTreeData] = useState<ITree | null>(null);
    const [initalizedTreeData, setInitalizedTreeData] =
        useState<boolean>(false);
    const [toggleState, setToggleState] = useState<string[]>([]);
    const [creatingTree, setCreatingTree] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- Variables --
    const showPersonnelNotes =
        !loadingPersonnel && 
        loadPersonnelSuccess && 
        !loadingNoteTypeNotes &&
        loadNoteTypeNotesSuccess &&
        !creatingTree;


    // Load personnel
    useEffect(() => {
        if (personnelIdNumber && !personnel) {
            setDrawerItems(PersonnelSubMenu(personnelIdNumber, permissions));
            setBreadcrumbs(PersonnelNotesBreadcrumbs(personnelIdNumber));

            setTreeData(null);

            loadPersonnel(personnelIdNumber);
            loadNoteTypeNotes(personnelIdNumber);
        }
    }, [
        personnelIdNumber,
        personnel,
        permissions,
        setDrawerItems,
        setBreadcrumbs,
        loadPersonnel,
        setTreeData,
        PersonnelSubMenu,
        PersonnelNotesBreadcrumbs,
        loadNoteTypeNotes
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadPersonnelFailure) {
            navigate(`/error/${loadPersonnelErrorCode}`);
        }
        if (loadNoteTypeNotesFailure) {
            navigate(`/error/${loadNoteTypeNotesErrorCode}`);
        }
    }, [
        loadPersonnelFailure, 
        loadPersonnelErrorCode, 
        loadNoteTypeNotesFailure, 
        loadNoteTypeNotesErrorCode, 
        navigate
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (personnelIdNumber && personnel?.firstName && personnel?.lastName) {
            setBreadcrumbs(
                PersonnelNotesBreadcrumbs(
                    personnelIdNumber,
                    `${personnel.firstName} ${personnel.lastName}`
                )
            );
        }
    }, [
        personnel,
        personnelIdNumber,
        permissions,
        setDrawerItems,
        setBreadcrumbs,
        PersonnelNotesBreadcrumbs,
    ]);

    // Cleanup effect to clear the personnel on component unmount
    useEffect(() => {
        return () => {
            setTreeData(null);
            clearPersonnel();
            clearNoteTypeNotes();
        };
    }, [clearPersonnel, clearNoteTypeNotes, setTreeData]);

    // Update tree
    useEffect(() => {
        setInitalizedTreeData(false);
    }, [noteTypeNotes, setInitalizedTreeData]);

    // Create tree
    useEffect(() => {
        if (noteTypeNotes && !initalizedTreeData) {
            setCreatingTree(true);

            // If no personnel notes return
            if (!noteTypeNotes || noteTypeNotes?.length == 0) {
                setCreatingTree(false);
                return;
            }

            const createChildNodes = (notes) => {
                let children = [];

                children =
                    notes
                        ?.sort((a, b) => a?.id - b?.id)
                        ?.map((y) => ({
                            id: `note-${y.id}`,
                            data: (
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: " 1fr 3fr 8fr",
                                    }}
                                >
                                    <div className="px-1">
                                        <FontAwesomeIcon
                                            icon={FontAwesomeIcons.Duotone.NOTE}
                                        />
                                    </div>
                                    <div className="px-1">{y?.title}</div>
                                    <div className="d-inline-block px-1">
                                        <span
                                            className="text-truncate"
                                            style={{ whiteSpace: "pre-line" }}
                                        >
                                            {y?.comments}
                                        </span>
                                    </div>
                                </div>
                            ),
                            icon: null,
                            collapsible: false,
                        })) ?? [];

                // Add table header
                if (children?.length > 0) {
                    children.unshift({
                        id: "tableheader",
                        data: (
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 3fr 8fr",
                                }}
                            >
                                <div className="px-1"></div>
                                <div className="px-1">
                                    <strong>Title</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Comment</strong>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: false,
                    });
                }

                return children;
            };

            let treeNodes =
                noteTypeNotes
                    ?.sort((a, b) =>
                        a?.noteType?.value.localeCompare(b?.noteType?.value)
                    )
                    ?.map((x) => ({
                        id: `type-${x.noteTypeId}`,
                        data: (
                            <div className="d-flex align-items-center">
                                <FontAwesomeIcon
                                    icon={FontAwesomeIcons.Duotone.NOTESMEDICAL}
                                />
                                <span
                                    className="pl-4 mb-0"
                                    style={{ width: "180px" }}
                                >
                                    {x.noteType?.value}
                                </span>
                                <div
                                    className="d-flex align-items-center justify-content-center"
                                    style={{ width: "50px" }}
                                >
                                    <span className="badge badge-pill badge-secondary mb-0">
                                        {x?.notes?.length}
                                    </span>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: true,
                        subTree: {
                            children: [...createChildNodes(x.notes)],
                        },
                    })) ?? [];

            // Initialize tree toggle
            const expanded = treeNodes.map((z) => z.id);

            setToggleState(expanded);
            setTreeData({ children: treeNodes });
            setInitalizedTreeData(true);
            setCreatingTree(false);
        }
    }, [
        noteTypeNotes,
        initalizedTreeData,
        treeData,
        setTreeData,
        setToggleState,
        setInitalizedTreeData,
        setCreatingTree,
    ]);

    const handleToggle = useCallback(
        (id: string, navigateFlag?: boolean) => {
            if (navigateFlag) {
                if (
                    id.includes("note") &&
                    AuthenticationHelper.hasPermission(
                        permissions,
                        Dtos.Permission.InstitutionNoteView
                    )
                ) {
                    navigate(
                        `/personnel/${personnelIdNumber}/notes/${id.replace(
                            "note-",
                            ""
                        )}`
                    );
                }
                if (id.includes("type")) {
                    setToggleState((prev) =>
                        prev.includes(id)
                            ? prev.filter((nodeId) => nodeId !== id)
                            : [...prev, id]
                    );
                }
            } else {
                setToggleState((prev) =>
                    prev.includes(id)
                        ? prev.filter((nodeId) => nodeId !== id)
                        : [...prev, id]
                );
            }
        },
        [setToggleState, navigate, personnelIdNumber, permissions, personnel]
    );

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-9 mb-2"}>
                        <h1>Personnel Notes</h1>
                    </div>
                    <div className="col-3" style={{ textAlign: "right" }}>
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.PersonnelNoteCreate
                        ) &&
                            personnel &&
                            personnel.id > 0 && (
                                <Link
                                    className={"btn btn-primary"}
                                    url={
                                        "/personnel/" +
                                        personnel.id +
                                        "/notes/create"
                                    }
                                >
                                    Create
                                </Link>
                            )}
                    </div>
                </div>
                {!showPersonnelNotes ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showPersonnelNotes ? (
                    <>
                        {treeData && treeData?.children?.length > 0 ? (
                            <Tree
                                treeData={treeData}
                                onToggle={handleToggle}
                                toggleState={toggleState}
                            />
                        ) : (
                            <div className="card card-shadow p-3">
                                <div className="alert alert-info d-flex mb-0">
                                    <div className="mr-2">
                                        <FontAwesomeIcon
                                            icon={
                                                FontAwesomeIcons.Solid
                                                    .INFO_SQUARE
                                            }
                                            fixedWidth
                                        />
                                    </div>
                                    <span>
                                        There are currently no personnel notes
                                    </span>
                                </div>
                            </div>
                        )}
                    </>
                ) : null}
            </div>
        );
    }, [
        permissions,
        personnel,
        showPersonnelNotes,
        treeData,
        handleToggle,
        toggleState,
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.PersonnelNoteView}
            loading={loadingPersonnel || loadingNoteTypeNotes}
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<PersonnelNotesPageProps> => {
    let personnel: Dtos.Personnel | undefined = !(
        state.personnel.formData instanceof Array
    )
        ? state.personnel.formData
        : undefined;
    let noteTypeNotes: Dtos.NoteTypeNotes[] | undefined = (state.noteTypeNotes.formData instanceof Array)
            ? state.noteTypeNotes.formData
            : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        personnel: personnel!,
        loadingPersonnel:
            state.personnel.loadState &&
            state.personnel.loadState.status == RequestState.Pending,
        loadPersonnelSuccess:
            state.personnel.loadState &&
            state.personnel.loadState.status == RequestState.Success,
        loadPersonnelFailure:
            state.personnel.loadState &&
            state.personnel.loadState.status == RequestState.Failure,
        loadPersonnelErrorCode: state.personnel.loadState.errorCode,
        noteTypeNotes: noteTypeNotes!,
        loadingNoteTypeNotes:
            state.noteTypeNotes.loadState &&
            state.noteTypeNotes.loadState.status == RequestState.Pending,
        loadNoteTypeNotesSuccess:
            state.noteTypeNotes.loadState &&
            state.noteTypeNotes.loadState.status == RequestState.Success,
        loadNoteTypeNotesFailure:
            state.noteTypeNotes.loadState &&
            state.noteTypeNotes.loadState.status == RequestState.Failure,
        loadNoteTypeNotesErrorCode: state.noteTypeNotes.loadState.errorCode,
        permissions: permissions,
    };
};

const mapDispatchToProps = (dispatch): Partial<PersonnelNotesPageProps> => {
    return {
        loadPersonnel: bindActionCreators(PersonnelActions.LoadPersonnelById, dispatch),
        clearPersonnel: bindActionCreators(PersonnelActions.Clear, dispatch),
        loadNoteTypeNotes: bindActionCreators(NoteTypesNotesActions.LoadNoteTypeNotesByPersonnelId, dispatch),
        clearNoteTypeNotes: bindActionCreators(NoteTypesNotesActions.Clear, dispatch),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch)
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(PersonnelNotesPage);
